import { BaseTelemetryEvent, ValidTelemetryEvent } from '~src/commonInfra/services/Telemetry';
import { FavoriteItemType } from '~src/favorites/types/IFavorite';
import { ChannelTypes } from '~src/home/types/ChannelTypes';
import { DisplayMode } from '~src/sessionManagement/serverApi/types/ISessionInfo';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { LayoutType } from '~src/spacesPlatform/state/LayoutType';
import { SiteSpaceContentType } from '~src/spacesPlatform/state/SiteSpaceContentTypeState';
import { SpaceType } from '~src/spacesPlatform/state/SpaceInfo';

import { ExplorerEventInfo } from './TelemetryEventCreator';

/**
 * All the event schemata in this file must be registered in self-service-analytics.tsi.lan:
 *
 * https://mytableau.tableaucorp.com/display/devcomm/VizPortal+Client+telemetry+events
 */

export type ExplorerEvent = ExplorerEventInfo & BaseTelemetryEvent;

export enum TelemetryEvents {
  AdvancedFilterPanelToggle = 'AdvancedFilterPanelToggle',
  DevicePreviewHidePreview = 'DevicePreview_HidePreview',
  DevicePreviewPreviewStateChange = 'DevicePreview_PreviewStateChange',
  DevicePreviewShowPreview = 'DevicePreview_ShowPreview',
  ExplorerDisplayMode = 'ExplorerDisplayMode',
  ExplorerOpenDetail = 'ExplorerOpenDetail',
  ExplorerSort = 'ExplorerSort',
  FavoriteItemOpened = 'FavoriteItemOpened',
  FavoritesMenuNavigateToFavorite = 'FavoritesMenuNavigateToFavorite',
  FavoritesMenuOpen = 'FavoritesMenuOpen',
  PreviewDeviceLayoutsLinkClicked = 'PreviewDeviceLayoutsLinkClicked',
  PublishCompleteDialogDisplayed = 'PublishCompleteDialogDisplayed',
  RedirectSource = 'redirect_source',
  SearchInitiated = 'SearchInitiated',
  SearchImpression = 'SearchImpression',
  SearchNoResult = 'SearchNoResult',
  SearchTermUpdated = 'SearchTermUpdated',
  SearchResultClicked = 'SearchResultClicked',
  SearchEPT = 'search_ept',
  SearchFiltersUpdated = 'search_filters_update',
  ShowExplorer = 'ShowExplorer',
  SiteSettingServerAdmin = 'SiteSettingServerAdmin',
  SiteSettingSiteAdmin = 'SiteSettingSiteAdmin',
  SiteSpaceContentTypeChange = 'SiteSpaceContentTypeChange',
  StarterClicked = 'StarterClicked',
  StartersDropdownConnectionTypeChanged = 'StartersDropdownConnectionTypeChanged',
  StarterWorkbookCreationClicked = 'StarterWorkbookCreationClicked',
  VizPortalNavigation = 'VizPortalNavigation',
  LeftNavLinkNavigation = 'LeftNavLinkNavigation',
  PageHeaderButtonClick = 'PageHeaderButtonClick',
  LeftNavCollapseExpandClick = 'LeftNavCollapseExpandClick',
  HomeApiPerformance = 'HomeApiPerformance',
  UnhandledError = 'UnhandledError',
  HomeSeeAllClick = 'HomeSeeAllClick',
  HomeWelcomeChannel = 'HomeWelcomeChannel',
  ContentClicked = 'ContentClicked',
  FavoriteToggleClicked = 'FavoriteToggleClicked',
  ActionMenuItemClicked = 'ActionMenuItemClicked',
  LoadMoreButtonClicked = 'LoadMoreButtonClicked',
  BuyLicenses = 'BuyLicenses',
  ShareDialogUserActions = 'ShareDialogUserActionsForVPC',
  UploadFlowEntryPoint = 'UploadFlowEntryPoint',
  UploadFlowDialogDetails = 'UploadFlowDialogDetails',
  UploadFlowActionDetails = 'UploadFlowActionDetails',
  UploadWorkbookEntryPoint = 'UploadWorkbookEntryPoint',
  UploadWorkbookDialogDetails = 'UploadWorkbookDialogDetails',
  UploadWorkbookActionDetails = 'UploadWorkbookActionDetails',
  CollectionInlineAction = 'CollectionInlineAction',
  IntlUserSettings = 'IntlUserSettings',
  VPCFeatureUsage = 'vpc_feature_usage',
  VPCFeatureEPT = 'vpc_feature_ept',
  // Note: It is not required to add your event types to this giant enum anymore.
  // See CreateContentTelemetry.ts for another pattern.
}

export type ContentTelemetryEvents = TelemetryEvents.ContentClicked | TelemetryEvents.FavoriteToggleClicked | TelemetryEvents.ActionMenuItemClicked;

export interface AdvancedFilterPanelToggleEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.AdvancedFilterPanelToggle;
  openOrClose: string;
  mixedContentEnabled: boolean;
}

export interface FavoriteItemOpenedEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.FavoriteItemOpened;
  favoriteItemType: FavoriteItemType;
  favoriteItemId: string;
}

export interface FavoritesMenuOpenEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.FavoritesMenuOpen;
  favoriteItemCount: number;
}

export interface FavoritesMenuNavigateToFavoriteEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.FavoritesMenuNavigateToFavorite;
  favoriteItemType: FavoriteItemType;
  favoriteItemId: string;
}

export interface PreviewDeviceLayoutsLinkClickedEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.PreviewDeviceLayoutsLinkClicked;
}

export interface PublishCompleteDialogDisplayedEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.PublishCompleteDialogDisplayed;
  isPreviewDeviceLayoutsLinkDisplayed: boolean;
}

export interface RedirectSourceEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.RedirectSource;
  source: string;
}

export interface SiteSpaceContentTypeChangeEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.SiteSpaceContentTypeChange;
  newContentType: SiteSpaceContentType;
  oldContentType: SiteSpaceContentType;
}

export interface StarterClicked extends ValidTelemetryEvent {
  connectionType: string;
  selectedStarterIndex: number;
  source: StartersDialogClickSourceOptions;
  starterId: string;
  starterName: string;
  type: TelemetryEvents.StarterClicked;
}

export interface IntlUserSettings extends ValidTelemetryEvent {
  Product: string;
  Action: string;
  User_Role: string;
  Language: string;
  Locale: string;
  Browser_Language: string;
  Browser_Type: string;
  type: TelemetryEvents.IntlUserSettings;
}

export interface StartersDropdownConnectionTypeChanged extends ValidTelemetryEvent {
  connectionType: string;
  type: TelemetryEvents.StartersDropdownConnectionTypeChanged;
}

export interface StarterWorkbookCreationClicked extends ValidTelemetryEvent {
  connectionType: string;
  source: StarterWorkbookCreationSourceOptions;
  starterId: string;
  starterName: string;
  type: TelemetryEvents.StarterWorkbookCreationClicked;
  withSampleData: boolean;
}
export interface VizPortalNavigationEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.VizPortalNavigation;
  newState: string;
  oldState: string;
  mixedContentEnabled: boolean;
  contentId: string;
}

export interface ExplorerSortEvent extends ValidTelemetryEvent {

  type: TelemetryEvents.ExplorerSort;
  sortField: string;
  ascending: boolean;
  eventSource: 'column' | 'dropdown';
}

export interface ExplorerOpenDetailEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.ExplorerOpenDetail;
  detailContentId: string;
  detailContentType: ExplorableItemType;
}

export interface ExplorerDisplayMode extends ValidTelemetryEvent {

  type: TelemetryEvents.ExplorerDisplayMode;
  previousMode: DisplayMode;
  newMode: DisplayMode;
}

export interface ShowExplorerEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.ShowExplorer;
}

export interface DevicePreviewShowPreviewEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.DevicePreviewShowPreview;
}

// Currently unused, but exists to keep parity with a previously-existing unused event in the DeprecatedEventWrapper
export interface DevicePreviewHidePreviewEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.DevicePreviewHidePreview;
}

export interface DevicePreviewPreviewStateChangeEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.DevicePreviewPreviewStateChange;
  preview_device_type: string;
}

export interface LeftNavLinkNavigationEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.LeftNavLinkNavigation;
  navLocation: string;
  previousSpace: SpaceType;
}

export interface PageHeaderButtonClickEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.PageHeaderButtonClick;
  button: 'modal-left-nav' | 'back-button' | 'help-menu' | 'alert-menu' | 'user-menu' | 'viz-viewer-search' | 'data-connections-tooltip';
  action: 'open' | 'close';
}

export interface LeftNavCollapseExpandClickEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.LeftNavCollapseExpandClick;
  action: 'collapse' | 'expand';
  layoutType: LayoutType;
}

export type SearchEventSource = 'omni' | 'search-space';

export interface SearchResultClickedEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.SearchResultClicked;
  eventSource: SearchEventSource;
  resultType: string;
  seeAll: boolean;
  index: number;
  total: number;
  searchTerm: string;
  contentId: string;
  searchFlowId: string;
}

export interface SearchNoResultEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.SearchNoResult;
  eventSource: SearchEventSource;
  searchTerm: string;
  searchFlowId: string;
}

export interface SearchInitiatedEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.SearchInitiated;
  eventSource: SearchEventSource;
  searchFlowId: string;
}

export interface SearchImpressionEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.SearchImpression;
  searchTerm: string;
  search_flow_id: string;
  impression_type: string;
}

export interface SearchTermUpdatedEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.SearchTermUpdated;
  eventSource: SearchEventSource;
  searchTerm: string;
  searchFlowId: string;
}

export interface SearchEPTEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.SearchEPT;
  ept_type: string;
  search_term: string;
  search_flow_id: string;
  duration_from_start_to_phase_a: number; // Phase A means initial rendering (like a loading indicator.)
  duration_from_phase_a_to_phase_b: number; // Phase B means the time when calling server side APIs
  duration_from_phase_b_to_phase_c: number; // Phase C means the time when all the server side responses are returned
  duration_from_phase_c_to_end: number; // End means the time when the final UI is rendered.
  total_duration: number;
  client_request_id?: string;
}

export interface SearchFiltersUpdatedEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.SearchFiltersUpdated;
  search_term: string;
  filters_added?: string;
  filters_removed?: string;
  filters_updated?: string;
}

export interface UnhandledErrorTelemetryEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.UnhandledError;
  error: string;
  errorInfo: string;
}

export interface SiteSettingServerAdminTelemetryEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.SiteSettingServerAdmin;
  adminCount_new: number;
  adminCount_old: number;
  authoringEnabled_new: boolean;
  authoringEnabled_old: boolean;
  availability_new: string;
  availability_old: string;
  cacheWarmupEnabled_new: boolean;
  cacheWarmupEnabled_old: boolean;
  catalogingOnSiteEnabled_new: boolean;
  catalogingOnSiteEnabled_old: boolean;
  contentVersionLimit_new: number;
  contentVersionLimit_old: number;
  contentVersionLimitEnabled_new: boolean;
  contentVersionLimitEnabled_old: boolean;
  currentLoggedInUserCount_new: number;
  currentLoggedInUserCount_old: number;
  customSubscriptionEmailEnabled_new: boolean;
  customSubscriptionEmailEnabled_old: boolean;
  customSubscriptionFooterEnabled_new: boolean;
  customSubscriptionFooterEnabled_old: boolean;
  flowsOnSiteEnabled_new: boolean;
  flowsOnSiteEnabled_old: boolean;
  guestAccessEnabled_new: boolean;
  guestAccessEnabled_old: boolean;
  licensedUserCount_new: number;
  licensedUserCount_old: number;
  creatorCount_new: number;
  creatorCount_old: number;
  explorerCount_new: number;
  explorerCount_old: number;
  viewerCount_new: number;
  viewerCount_old: number;
  metricsEnabled_new: boolean;
  metricsEnabled_old: boolean;
  siteAdminsCanAddUsers_new: boolean;
  siteAdminsCanAddUsers_old: boolean;
  storageQuotaBytes_new: number;
  storageQuotaBytes_old: number;
  storageQuotaEnabled_new: boolean;
  storageQuotaEnabled_old: boolean;
  storageQuotaPercentUsed_new: number;
  storageQuotaPercentUsed_old: number;
  storageUsedBytes_new: number;
  storageUsedBytes_old: number;
  userCount_new: number;
  userCount_old: number;
  userQuota_new: number;
  userQuota_old: number;
  userQuotaMode_new: string;
  userQuotaMode_old: string;
  versionHistoryEnabled_new: boolean;
  versionHistoryEnabled_old: boolean;
  runNowEnabled_new: boolean;
  runNowEnabled_old: boolean;
  eventid: string;
  siteId: string;
  tierCreatorCapacity_new: number;
  tierCreatorCapacity_old: number;
  tierExplorerCapacity_new: number;
  tierExplorerCapacity_old: number;
  tierViewerCapacity_new: number;
  tierViewerCapacity_old: number;
}

export interface SiteSettingSiteAdminTelemetryEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.SiteSettingSiteAdmin;
  add_support_user_enabled_new: boolean;
  add_support_user_enabled_old: boolean;
  admin_insights_publish_frequency_new: number;
  admin_insights_publish_frequency_old: number;
  allow_subscription_attachments_new?: boolean;
  allow_subscription_attachments_old?: boolean;
  alternative_federation_engine_enabled_new: boolean;
  alternative_federation_engine_enabled_old: boolean;
  collections_enabled_new: boolean;
  collections_enabled_old: boolean;
  commenting_enabled_new?: boolean;
  commenting_enabled_old?: boolean;
  commenting_mentions_enabled_new?: boolean;
  commenting_mentions_enabled_old?: boolean;
  custom_schedule_subscription_for_site_enabled_new?: boolean;
  custom_schedule_subscription_for_site_enabled_old?: boolean;
  data_alerts_enabled_new: boolean;
  data_alerts_enabled_old: boolean;
  dashboard_narrative_enabled_new: boolean;
  dashboard_narrative_enabled_old: boolean;
  dashboard_qa_enabled_new: boolean;
  dashboard_qa_enabled_old: boolean;
  derived_permissions_enabled_new: boolean;
  derived_permissions_enabled_old: boolean;
  data_orientation_enabled_new: boolean;
  data_orientation_enabled_old: boolean;
  extract_encryption_mode_new: string;
  extract_encryption_mode_old: string;
  generative_ai_web_authoring_new: boolean;
  generative_ai_web_authoring_old: boolean;
  generative_ai_web_authoring_on_prem_enabled_new: boolean;
  generative_ai_web_authoring_on_prem_enabled_old: boolean;
  is_admin_data_source_enabled_new: boolean;
  is_admin_data_source_enabled_old: boolean;
  named_sharing_enabled_new: boolean;
  named_sharing_enabled_old: boolean;
  notification_enabled_new: boolean;
  notification_enabled_old: boolean;
  notification_for_client_not_running_enabled_new?: boolean;
  notification_for_client_not_running_enabled_old?: boolean;
  personal_space_enabled_new: boolean;
  personal_space_enabled_old: boolean;
  personal_space_storage_quota_set_new: boolean;
  personal_space_storage_quota_set_old: boolean;
  personal_space_storage_quota_new: number;
  personal_space_storage_quota_old: number;
  refresh_token_enabled_new: boolean;
  refresh_token_enabled_old: boolean;
  remote_querying_enabled_new: boolean;
  remote_querying_enabled_old: boolean;
  request_access_enabled_new: boolean;
  request_access_enabled_old: boolean;
  server_allow_subscription_attachments_new?: boolean;
  server_allow_subscription_attachments_old?: boolean;
  sheet_image_enabled_new: boolean;
  sheet_image_enabled_old: boolean;
  site_invite_email_enabled_new: boolean;
  site_invite_email_enabled_old: boolean;
  subscribe_others_enabled_new?: boolean;
  subscribe_others_enabled_old?: boolean;
  subscriptions_enabled_new?: boolean;
  subscriptions_enabled_old?: boolean;
  user_visibility_new: string;
  user_visibility_old: string;
  generative_ai_enabled_new: boolean;
  generative_ai_enabled_old: boolean;
  viz_recommendations_enabled_new: boolean;
  viz_recommendations_enabled_old: boolean;
  viz_rec_username_visibility_enabled_new: boolean;
  viz_rec_username_visibility_enabled_old: boolean;
  pulse_enabled_new?: boolean;
  pulse_enabled_old?: boolean;
  pulse_group_id_new?: string;
  pulse_group_id_old?: string;
  eventid: string;
  siteId: string;
  flow_federation_strategy_selected_connection_enabled_new: boolean;
  flow_federation_strategy_selected_connection_enabled_old: boolean;
}

export interface BuyLicensesTelemetryEvent
  extends ValidTelemetryEvent {
  type: TelemetryEvents.BuyLicenses;
  action: 'buy-licenses-button-1-displayed' | 'buy-licenses-button-1-clicked' |
  'buy-licenses-button-2-displayed' | 'buy-licenses-button-2-clicked' |
  'buy-licenses-button-1-submit' | 'buy-licenses-button-2-submit' |
  'buy-licenses-button-3-clicked' | 'buy-licenses-button-3-submit' | 'buy-licenses-button-3-clickthrough' |
  'buy-licenses-button-3-submit-failed' | 'buy-licenses-button-3-clickthrough-failed';
  siteUuid?: string;
}

export enum ShareDialogUserActionOptions {
  CloseShareDialog = 'Close',
  CopyEmbedCode = 'Copy embed code',
  CopyLink = 'Copy link',
  OpenShareDialog = 'Open',
  ShareContent = 'Share content',
  ContentCleared = 'Content cleared'
}

export enum StartersDialogClickSourceOptions {
  Dialog = 'Dialog',
  HomeCard = 'HomeCard',
  HomeCardMoreLikeThis = 'HomeCardMoreLikeThis',
  Space = 'Space',
}

export enum StarterWorkbookCreationSourceOptions {
  Dialog = 'Dialog',
  Space = 'Space',
}

export interface ShareDialogUserActionsEvent extends ValidTelemetryEvent {
  // Ensure that property names matches exactly with the schema including their CASE
  // otherwise events will not be recorded
  type: TelemetryEvents.ShareDialogUserActions;
  UserActionType: ShareDialogUserActionOptions;
  Args?: string;
}

export type CollectionInlineActionType = 'rename' | 'set-description';

export interface CollectionInlineActionEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.CollectionInlineAction;
  actionType: CollectionInlineActionType;
  collection_id: string;
}

export interface LoadMoreButtonClickedEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.LoadMoreButtonClicked;
  channelType: ChannelTypes;
}

export type VPCFeatureUsageType = 'impression' | 'click_through';

export interface VPCFeatureUsageEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.VPCFeatureUsage;
  feature_name: string;
  usage_type: VPCFeatureUsageType;
  extra_parameter_a?: string;
  extra_parameter_b?: string;
  extra_parameter_c?: number;
  extra_parameter_d?: number;
}

export interface VPCFeatureEPTEvent extends ValidTelemetryEvent {
  type: TelemetryEvents.VPCFeatureEPT;
  feature_name: string;
  duration: number;
  data_size?: number;
}